// src/Views/LandingPage.js
import React, { useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { StoreContext } from '../contexts/StoreContext';
import { BannersContext } from '../contexts/BannersContext';
import { HomePreferencesContext } from '../contexts/HomePreferencesContext';
import { useSocialMedia } from '../contexts/SocialMediaContext';
import PortfolioGallery from './PortfolioGallery';
import ContactForm from './ContactForm';
import usePageView from '../hooks/usePageView';
import { MAX_CONTENT_WIDTH, BORDER_RADIUS, SPACING_MICRO, SPACING_TIGHT, SPACING_ELEMENT, SPACING_COMPACT, SPACING_COMPONENT, SPACING_BUTTON, SPACING_CARD, SPACING_CARD_BODY, SPACING_GROUP, SPACING_SECTION, SPACING_SECTION_LARGE, SPACING_HERO, SPACING_PAGE, SPACING_PAGE_LARGE } from '../constants/layout';
import {
  CODA_EXTRABOLD_DISPLAY_SIZE,
  FONT_CAPTION,
  FONT_TITLE,
  FONT_TITLE_MOBILE,
  FONT_TITLE2,
  FONT_TITLE2_MOBILE,
  FONT_TITLE3,
  FONT_HEADLINE,
  FONT_HEADLINE_MOBILE,
  FONT_SUBHEADLINE,
  FONT_BODY,
} from '../constants/typography';

function InstagramSection() {
  const { socialMedia } = useSocialMedia();
  const handle = socialMedia?.instagram?.trim();

  const profileUrl = handle
    ? (handle.startsWith('http')
        ? (handle.endsWith('/') ? handle : `${handle}/`)
        : `https://www.instagram.com/${handle}/`)
    : null;
  const embedPermalink = profileUrl
    ? `${profileUrl}?utm_source=ig_embed&utm_campaign=loading`
    : null;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!embedPermalink) return;

    const scriptSrc = 'https://www.instagram.com/embed.js';
    const processEmbeds = () => {
      if (window.instgrm?.Embeds?.process) {
        window.instgrm.Embeds.process();
      }
    };

    let script = document.querySelector(`script[src="${scriptSrc}"]`);
    const handleLoad = () => {
      script?.setAttribute('data-instgrm-loaded', 'true');
      processEmbeds();
    };

    if (script) {
      if (script.getAttribute('data-instgrm-loaded') === 'true') {
        processEmbeds();
      } else {
        script.addEventListener('load', handleLoad);
      }
    } else {
      script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.addEventListener('load', handleLoad);
      script.addEventListener('error', () => {});
      document.body.appendChild(script);
    }

    // Suppress cross-origin "Script error." from Instagram embed
    const suppressScriptError = (event) => {
      if (!event.message || event.message === 'Script error.') {
        event.stopImmediatePropagation();
        event.preventDefault();
        return true;
      }
    };
    window.addEventListener('error', suppressScriptError, true);

    return () => {
      script?.removeEventListener('load', handleLoad);
      window.removeEventListener('error', suppressScriptError);
    };
  }, [embedPermalink]);

  if (!profileUrl || !embedPermalink) return null;

  return (
    <section className="instagram-section">
      <div className="instagram-inner">
        <div className="instagram-copy">
          <h2 className="instagram-title">Stay Connected</h2>
          <p className="instagram-subtitle">
            Project transformations, before & afters, and behind-the-scenes moments.
          </p>
          <a
            className="instagram-button"
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg className="instagram-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
            Follow us
          </a>
        </div>
        <div className="instagram-embed-wrapper">
          <blockquote
            key={embedPermalink}
            className="instagram-media"
            data-instgrm-permalink={embedPermalink}
            data-instgrm-version="14"
          >
            <a
              href={embedPermalink}
              target="_blank"
              rel="noopener noreferrer"
            >
              View this profile on Instagram
            </a>
          </blockquote>
        </div>
      </div>

      <style jsx>{`
        .instagram-section {
          background: linear-gradient(135deg, #405DE6 0%, #5851DB 15%, #833AB4 30%, #C13584 45%, #E1306C 60%, #FD1D1D 75%, #F77737 90%, #FCAF45 100%);
          padding: ${SPACING_PAGE} ${SPACING_CARD};
          padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
          padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
          display: flex;
          justify-content: center;
          border-bottom: 1px solid var(--color-border);
        }

        .instagram-inner {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: ${SPACING_SECTION_LARGE};
          align-items: center;
          width: 100%;
          max-width: ${MAX_CONTENT_WIDTH};
          color: #ffffff;
        }

        .instagram-copy {
          display: flex;
          flex-direction: column;
          gap: ${SPACING_TIGHT};
        }

        .instagram-title {
          margin: 0;
          font-size: ${FONT_TITLE};
          font-weight: 700;
          letter-spacing: 0;
        }

        .instagram-subtitle {
          margin: 0;
          color: rgba(255, 255, 255, 0.75);
          line-height: 1.6;
        }

        .instagram-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: ${SPACING_COMPACT};
          margin-top: calc(${SPACING_CARD} - ${SPACING_TIGHT});
          padding: ${SPACING_BUTTON} ${SPACING_GROUP};
          border-radius: 6px;
          background: #ffffff;
          color: #C13584;
          font-weight: 600;
          text-decoration: none;
          width: fit-content;
          transition: background 0.2s ease, transform 0.2s ease;
        }

        .instagram-icon {
          width: 20px;
          height: 20px;
        }

        .instagram-button:hover,
        .instagram-button:focus {
          background: #f0f0f0;
          transform: translateY(-1px);
        }

        .instagram-embed-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .instagram-embed-wrapper :global(blockquote.instagram-media) {
          margin: 0 !important;
          background-color: #ffffff !important;
          border-radius: 12px !important;
          border: none !important;
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.25) !important;
          overflow: hidden;
          max-width: 420px !important;
          width: 100% !important;
        }

        @media (max-width: 960px) {
          .instagram-inner {
            grid-template-columns: 1fr;
            text-align: center;
          }

          .instagram-copy {
            align-items: center;
          }

          .instagram-button {
            width: auto;
          }
        }

        @media (max-width: 600px) {
          .instagram-section {
            padding: ${SPACING_PAGE} ${SPACING_CARD};
            padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
            padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
          }

          .instagram-title {
            font-size: ${FONT_TITLE_MOBILE};
          }
        }
      `}</style>
    </section>
  );
}

function getContrastColor(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

function BannersSection() {
  const { banners } = useContext(BannersContext);

  if (!banners || banners.length === 0) return null;

  return (
    <div className="banners-section">
      {banners.map((banner) => {
        const hasBg = !!banner.backgroundColor;
        const textColor = hasBg ? getContrastColor(banner.backgroundColor) : undefined;
        const subtitleColor = hasBg ? (textColor === '#FFFFFF' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.6)') : undefined;
        return (
          <div
            key={banner.id}
            className="banner-item"
            style={{
              backgroundColor: banner.backgroundColor || 'transparent',
              borderBottom: hasBg ? 'none' : undefined,
            }}
          >
            {banner.title && <h2 className="banner-title" style={textColor ? { color: textColor } : undefined}>{banner.title}</h2>}
            {banner.subtitle && <p className="banner-subtitle" style={subtitleColor ? { color: subtitleColor } : undefined}>{banner.subtitle}</p>}
          </div>
        );
      })}
      <style jsx>{`
        .banners-section {
          width: 100vw;
          margin-left: calc(-50vw + 50%);
          display: flex;
          flex-direction: column;
          gap: 0;
        }
        .banner-item {
          padding: ${SPACING_HERO} ${SPACING_CARD};
          text-align: center;
          border-bottom: 1px solid var(--color-border);
        }
        .banner-title {
          margin: 0 0 ${SPACING_COMPACT} 0;
          font-size: ${FONT_TITLE2};
          font-weight: 700;
          color: var(--color-text-primary);
        }
        .banner-subtitle {
          margin: 0 auto;
          font-size: ${FONT_HEADLINE};
          font-weight: 300;
          color: var(--color-text-secondary);
          max-width: ${MAX_CONTENT_WIDTH};
          line-height: 1.5;
        }
        @media (max-width: 768px) {
          .banner-item {
            padding: ${SPACING_SECTION} ${SPACING_CARD};
          }
          .banner-title {
            font-size: ${FONT_TITLE2_MOBILE};
          }
          .banner-subtitle {
            font-size: ${FONT_HEADLINE_MOBILE};
          }
        }
      `}</style>
    </div>
  );
}

export default function LandingPage() {
  usePageView('home');
  const { store, loading } = useContext(StoreContext);
  const { homePreferences } = useContext(HomePreferencesContext);

  const phoneNumber = store?.storeContact?.phoneNumber?.trim();
  const phoneHref = phoneNumber ? `tel:${phoneNumber.replace(/[^+\d]/g, '')}` : '';

  // Scroll to contact section
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner" />
        <style jsx>{`
          .loading-container {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 400px;
          }
          .spinner {
            width: 40px;
            height: 40px;
            border: 4px solid var(--color-border);
            border-top-color: var(--color-text-primary);
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">{homePreferences?.heroTitle || 'Do Your Floors Look Dull, Dirty, or Outdated?'}</h1>
          <p className="hero-subtitle">{homePreferences?.heroSubtitle || 'Whether travertine or another material, our team is ready to restore your floor and provide a brilliant, long-lasting finish.'}</p>
          <Link to="/ourwork" className="featured-floors-grid">
            <div className="featured-floor-item">
              <img src="/images/featured-vct.jpg" alt="VCT Floor" className="featured-floor-img" onLoad={e => e.target.classList.add('loaded')} />
            </div>
            <div className="featured-floor-item">
              <img src="/images/featured-wood.jpg" alt="Wood Floor" className="featured-floor-img" onLoad={e => e.target.classList.add('loaded')} />
            </div>
            <div className="featured-floor-item">
              <img src="/images/featured-travertine.jpg" alt="Travertine Floor" className="featured-floor-img" onLoad={e => e.target.classList.add('loaded')} />
            </div>
          </Link>
        </div>
      </section>

      <section className="service-area-section">
        <div className="container service-area-content">
          <h2 className="service-area-title">
            <span className="service-area-kicker">Proudly serving</span>
            <span className="service-area-region">Orange County</span>
          </h2>
          <button onClick={scrollToContact} className="service-area-cta-button">
            Request Free Estimate
          </button>
        </div>
      </section>

      <BannersSection />

      {/* Gallery Section */}
      <section className="gallery-section">
        <div className="container">
          <h2 className="section-heading">Our Work</h2>
          <p className="section-description">
            After restoring the floors in the images below, you can notice the difference and the aesthetic we were able to achieve with an attractive finish.
          </p>
          <PortfolioGallery limit={16} viewMorePath="/ourwork" limitRows />
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <h2 className="section-heading">Why Restore Your Floors</h2>
          <div className="benefits-grid">
            <div className="benefit-item">
              <span className="material-icons benefit-icon benefit-icon-sparkle">auto_awesome</span>
              <h3 className="benefit-title">A Stunning Interior</h3>
              <p className="benefit-description">
                Floors cover a large area of your home. A clean floor serves as the foundation for all other design elements, creating a sense of order and cleanliness.
              </p>
            </div>
            <div className="benefit-item">
              <span className="material-icons benefit-icon benefit-icon-clock">schedule</span>
              <h3 className="benefit-title">Long Lasting Cleanliness</h3>
              <p className="benefit-description">
                With proper care, floor sealer lasts 6-12 months or more. Ongoing maintenance preserves the clean look.
              </p>
            </div>
            <div className="benefit-item">
              <span className="material-icons benefit-icon benefit-icon-shield">shield</span>
              <h3 className="benefit-title">Extended Floor Durability</h3>
              <p className="benefit-description">
                Floor sealer protects against spills, dirt, and grime. Without it, floors can crack or discolor, leading to costly repairs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <div className="container">
          <div className="process-header">
            <h2 className="section-heading process-heading">Our Two Step Process</h2>
          </div>
          <div className="process-journey">
            <article className="process-step">
              <span className="process-step-number">1.</span>
              <div className="process-step-content">
                <h3 className="process-step-title">Deep Scrubbing</h3>
                <p className="process-step-description">
                  Using specialized equipment and solutions to remove accumulated dirt
                </p>
              </div>
            </article>
            <div className="process-connector" aria-hidden="true">
              <span className="material-icons">east</span>
            </div>
            <article className="process-step">
              <span className="process-step-number">2.</span>
              <div className="process-step-content">
                <h3 className="process-step-title">Sealing</h3>
                <p className="process-step-description">
                  Choice between shiny or natural finishes for ongoing protection
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Credibility Section */}
      <section className="credibility-section">
        <div className="container">
          <div className="credibility-grid">
            <div className="credibility-item">
              <div className="credibility-icon-container credibility-bg-blue">
                <span className="credibility-number">15+</span>
              </div>
              <h3 className="credibility-text">Years of Experience</h3>
            </div>
            <div className="credibility-item">
              <div className="credibility-icon-container credibility-bg-purple">
                <span className="material-icons credibility-icon">business</span>
              </div>
              <h3 className="credibility-text">Commercial And Residential Floor Restoration</h3>
            </div>
            <div className="credibility-item">
              <div className="credibility-icon-container credibility-bg-amber">
                <span className="material-icons credibility-icon">star</span>
              </div>
              <h3 className="credibility-text">Hundreds of Happy Customers</h3>
            </div>
            <a
              href="https://www.homeadvisor.com/rated.floorjanitorialcom.142647154.html"
              target="_blank"
              rel="noopener noreferrer"
              className="credibility-item credibility-link"
            >
              <div className="credibility-icon-container credibility-bg-green">
                <span className="material-icons credibility-icon">verified</span>
              </div>
              <h3 className="credibility-text">5 Stars on HomeAdvisor</h3>
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="testimonials-container">
          <div className="testimonials-copy">
            <h2 className="section-heading testimonials-heading">What Our Clients Say</h2>
            <p className="section-description testimonials-description">
              Trusted by businesses across Orange County for reliable floor cleaning services.
            </p>
          </div>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-author-section">
                <p className="testimonial-name">John P.</p>
                <p className="testimonial-location">Mission Viejo, CA</p>
              </div>
              <p className="testimonial-text">
                "I recently had the pleasure of working with Jonathan and his father from Floor Janitorial, and I can't say enough about the <mark className="highlight">outstanding service they provided</mark>... Their <mark className="highlight">attention to detail was remarkable</mark>, and it was clear that customer satisfaction was their top priority."
              </p>
              <div className="testimonial-rating">
                <span className="material-icons star-icon">star</span>
                <span className="material-icons star-icon">star</span>
                <span className="material-icons star-icon">star</span>
                <span className="material-icons star-icon">star</span>
                <span className="material-icons star-icon">star</span>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-author-section">
                <p className="testimonial-name">Juan M.</p>
                <p className="testimonial-location">Mission Viejo, CA</p>
              </div>
              <p className="testimonial-text">
                "Jose and Jonathan, his son did an awesome job at my home. They cleaned, buffed and polished all the travertine floor downstairs. I have <mark className="highlight">never seen our floor more shiny</mark>. My wife and I are very happy with the work they did and will <mark className="highlight">highly recommend them to neighbors and friends</mark>. Very satisfied with their work."
              </p>
              <div className="testimonial-rating">
                <span className="material-icons star-icon">star</span>
                <span className="material-icons star-icon">star</span>
                <span className="material-icons star-icon">star</span>
                <span className="material-icons star-icon">star</span>
                <span className="material-icons star-icon">star</span>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-author-section">
                <p className="testimonial-name">Barbara M.</p>
                <p className="testimonial-location">Huntington Beach, CA</p>
              </div>
              <p className="testimonial-text">
                "The tile and wood floors were <mark className="highlight">scrubbed very thoroughly and sealed</mark>. They look <mark className="highlight">shiny and clean</mark>."
              </p>
              <div className="testimonial-rating">
                <span className="material-icons star-icon">star</span>
                <span className="material-icons star-icon">star</span>
                <span className="material-icons star-icon">star</span>
                <span className="material-icons star-icon">star</span>
                <span className="material-icons star-icon">star</span>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-author-section">
                <p className="testimonial-name">Hamid G.</p>
                <p className="testimonial-location">Mission Viejo, CA</p>
              </div>
              <p className="testimonial-text">
                "Jonathan and his father helped us clean and polish our 2000 SF travertine flooring. The <mark className="highlight">floors look great</mark>, and we received <mark className="highlight">many compliments from friends and neighbors</mark>…"
              </p>
              <div className="testimonial-rating">
                <span className="material-icons star-icon">star</span>
                <span className="material-icons star-icon">star</span>
                <span className="material-icons star-icon">star</span>
                <span className="material-icons star-icon">star</span>
                <span className="material-icons star-icon">star</span>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-author-section">
                <p className="testimonial-name">Mario Molina</p>
              </div>
              <p className="testimonial-text">
                "<mark className="highlight">On time</mark> and <mark className="highlight">excellent attention to details</mark>. Thank you... The <mark className="highlight">tile floors look fantastic</mark> after being done…"
              </p>
              <div className="testimonial-rating">
                <span className="material-icons star-icon">star</span>
                <span className="material-icons star-icon">star</span>
                <span className="material-icons star-icon">star</span>
                <span className="material-icons star-icon">star</span>
                <span className="material-icons star-icon">star</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact-section" className="contact-section">
        <div className="container">
          <div className="contact-wrapper">
            <div className="contact-info">
              <h2 className="contact-heading">See If We Can Help</h2>
              <p className="contact-description">
                {phoneNumber ? (
                  <>Give us a call at <a href={phoneHref} className="phone-link">{phoneNumber}</a> or send us a message and we'll get back to you.</>
                ) : (
                  <>Give us a call or send us a message and we'll get back to you.</>
                )}
              </p>
              <ul className="contact-checklist">
                <li><span className="material-icons check-icon">check</span>We'll answer your questions and explain our process</li>
                <li><span className="material-icons check-icon">check</span>We'll be transparent about whether we can work on your floors</li>
                <li><span className="material-icons check-icon">check</span>No commitment required</li>
                <li><span className="material-icons check-icon">check</span>Schedule a free in-person consultation or demo</li>
              </ul>
            </div>
            <ContactForm />
          </div>
        </div>
      </section>

      {/* Instagram Section */}
      <InstagramSection />

      <style jsx>{`
        .landing-page {
          width: 100%;
          --content-max-width: ${MAX_CONTENT_WIDTH};
          --content-gutter: clamp(16px, 2.3vw, 28px);
          --section-padding-y: ${SPACING_SECTION_LARGE};
          --section-padding-y-mobile: ${SPACING_SECTION};
          --section-gap-xl: ${SPACING_SECTION_LARGE};
          --section-gap-lg: ${SPACING_SECTION};
          --section-gap-md: ${SPACING_GROUP};
          --section-gap-sm: ${SPACING_CARD};
        }

        .container {
          max-width: var(--content-max-width);
          margin: 0 auto;
          padding: 0 var(--content-gutter);
          padding-left: max(var(--content-gutter), env(safe-area-inset-left));
          padding-right: max(var(--content-gutter), env(safe-area-inset-right));
        }

        /* Hero Section */
        .hero-section {
          background: linear-gradient(160deg, rgb(55, 105, 160) 0%, rgb(90, 152, 205) 100%);
          padding: ${SPACING_HERO} var(--content-gutter);
          padding-left: max(var(--content-gutter), env(safe-area-inset-left));
          padding-right: max(var(--content-gutter), env(safe-area-inset-right));
          text-align: center;
          border-bottom: none;
        }

        .hero-content {
          max-width: var(--content-max-width);
          margin: 0 auto;
        }

        .hero-title {
          font-size: 2.4rem;
          font-weight: 800;
          color: #ffffff;
          margin: 0 auto ${SPACING_CARD} auto;
          line-height: 1.1;
          letter-spacing: 0;
          max-width: 840px;
        }

        .hero-subtitle {
          font-size: ${FONT_HEADLINE};
          font-weight: 500;
          color: rgba(255, 255, 255, 0.75);
          margin: 0 auto;
          letter-spacing: 0.02em;
          line-height: 1.5;
          max-width: 840px;
        }

        .service-area-section {
          background: linear-gradient(
            90deg,
            rgba(245, 158, 11, 0.14) 0%,
            rgba(245, 158, 11, 0.06) 45%,
            var(--color-surface) 100%
          );
          padding: ${SPACING_HERO} var(--content-gutter);
          padding-left: max(var(--content-gutter), env(safe-area-inset-left));
          padding-right: max(var(--content-gutter), env(safe-area-inset-right));
          border-bottom: 1px solid #f3d2a3;
        }

        .service-area-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: ${SPACING_GROUP};
        }

        .service-area-title {
          margin: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: ${SPACING_TIGHT};
          color: #B45309;
          line-height: 1.05;
        }

        .service-area-kicker {
          font-size: ${FONT_CAPTION};
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: #92400E;
        }

        .service-area-region {
          font-size: ${FONT_TITLE};
          font-weight: 800;
          letter-spacing: 0.01em;
        }

        .service-area-cta-button {
          background-color: #B45309;
          color: #ffffff;
          border: none;
          padding: ${SPACING_BUTTON} ${SPACING_CARD};
          min-width: 260px;
          font-size: ${FONT_BODY};
          font-weight: 600;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 6px 16px rgba(180, 83, 9, 0.32);
          letter-spacing: 0.01em;
        }

        .service-area-cta-button:hover {
          background-color: #92400E;
          transform: translateY(-1px);
          box-shadow: 0 8px 22px rgba(146, 64, 14, 0.35);
        }

        .service-area-cta-button:active {
          transform: translateY(0);
        }

        .hero-section .featured-floors-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: clamp(12px, 1.6vw, 20px);
          border-radius: 0;
          overflow: visible;
          margin-top: ${SPACING_SECTION};
          margin-left: auto;
          margin-right: auto;
          max-width: min(var(--content-max-width), 1020px);
          width: 100%;
        }

        .hero-section .featured-floor-item {
          overflow: hidden;
          aspect-ratio: 4 / 5;
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.35);
          box-shadow: 0 12px 30px rgba(15, 23, 42, 0.22);
          background-color: #1e293b;
          background-image: linear-gradient(90deg, #1e293b 0%, #2d3a4f 50%, #1e293b 100%);
          background-size: 200% 100%;
          animation: shimmer 1.5s ease-in-out infinite;
        }

        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        .hero-section .featured-floor-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
          opacity: 0;
          transition: transform 0.4s ease, opacity 0.6s ease;
        }

        .hero-section .featured-floor-img.loaded {
          opacity: 1;
        }

        .hero-section .featured-floor-item:hover .featured-floor-img {
          transform: scale(1.08);
        }

        .hero-image-placeholder {
          background-color: var(--color-surface-muted);
          border: 2px dashed var(--color-border);
          border-radius: 12px;
          padding: ${SPACING_PAGE_LARGE} ${SPACING_CARD_BODY};
          margin: 0 auto;
          max-width: 600px;
        }

        .placeholder-text {
          font-size: ${FONT_TITLE3};
          color: var(--color-text-secondary);
          margin: 0 0 ${SPACING_CARD} 0;
        }

        .scroll-prompt {
          font-size: ${FONT_BODY};
          color: var(--color-text-secondary);
          margin: 0;
          animation: bounce 2s infinite;
        }

        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-10px);
          }
          60% {
            transform: translateY(-5px);
          }
        }


        /* Section Styling */
        section {
          padding: var(--section-padding-y) var(--content-gutter);
          padding-left: max(var(--content-gutter), env(safe-area-inset-left));
          padding-right: max(var(--content-gutter), env(safe-area-inset-right));
          border-bottom: 1px solid var(--color-border);
        }

        .section-heading {
          font-size: ${FONT_TITLE};
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0 0 ${SPACING_SECTION} 0;
          text-align: center;
          line-height: 1.2;
          letter-spacing: 0;
        }

        .gallery-section .section-heading {
          margin-bottom: ${SPACING_TIGHT};
        }

        .section-description {
          font-size: ${FONT_BODY};
          color: var(--color-text-secondary);
          text-align: center;
          margin: 0 auto ${SPACING_GROUP} auto;
          max-width: 820px;
          line-height: 1.65;
          margin-left: auto;
          margin-right: auto;
        }

        .phone-link {
          color: var(--color-accent);
          text-decoration: none;
          font-weight: 500;
        }

        .phone-link:hover {
          text-decoration: underline;
        }

        /* Gallery Section */
        .gallery-cta {
          text-align: center;
          margin-top: ${SPACING_SECTION};
        }

        .gallery-cta-button {
          display: inline-block;
          padding: ${SPACING_BUTTON} ${SPACING_SECTION};
          font-size: ${FONT_BODY};
          font-weight: 600;
          color: var(--color-accent);
          background: transparent;
          border: 2px solid var(--color-accent);
          border-radius: 8px;
          text-decoration: none;
          transition: all 0.2s ease;
        }

        .gallery-cta-button:hover {
          background: var(--color-accent);
          color: var(--color-accent-text);
        }

        /* Benefits Section */
        .benefits-section {
          background-color: var(--color-card-background);
        }

        .benefits-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: var(--section-gap-lg);
          align-items: start;
        }

        .benefit-item {
          text-align: center;
          padding: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          box-sizing: border-box;
          position: relative;
        }


        .benefit-icon {
          font-size: 48px;
          margin-bottom: ${SPACING_BUTTON};
        }

        .benefit-icon-sparkle {
          color: #F59E0B;
        }

        .benefit-icon-clock {
          color: #3B82F6;
        }

        .benefit-icon-shield {
          color: #10B981;
        }

        .benefit-title {
          font-size: ${FONT_TITLE3};
          font-weight: 600;
          color: var(--color-text-primary);
          margin: 0 0 ${SPACING_COMPONENT} 0;
        }

        .benefit-description {
          font-size: ${FONT_BODY};
          color: var(--color-text-secondary);
          margin: 0;
          line-height: 1.6;
        }

        /* Process Section */
        .process-section {
          background:
            radial-gradient(circle at 12% 18%, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0) 48%),
            radial-gradient(circle at 88% 82%, rgba(16, 185, 129, 0.07) 0%, rgba(16, 185, 129, 0) 42%),
            linear-gradient(180deg, var(--color-card-background) 0%, var(--color-surface) 100%);
        }

        .process-header {
          max-width: none;
          margin: 0 auto;
          text-align: center;
        }

        .process-kicker {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: ${SPACING_ELEMENT} ${SPACING_BUTTON};
          border-radius: 999px;
          border: 1px solid var(--color-border);
          background: var(--color-surface);
          color: var(--color-accent);
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: ${SPACING_CARD};
        }

        .process-heading {
        }

        .process-intro {
          margin: 0;
          color: var(--color-text-secondary);
          font-size: ${FONT_BODY};
          line-height: 1.7;
        }

        .process-journey {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
          gap: var(--section-gap-lg);
          align-items: start;
          max-width: none;
          margin: 0 auto;
        }

        .process-step {
          padding: ${SPACING_TIGHT} 0;
          display: flex;
          align-items: flex-start;
          gap: ${SPACING_CARD};
        }

        .process-step-number {
          display: block;
          flex: 0 0 84px;
          color: #111111;
          font-size: clamp(2.8rem, 5.2vw, 4.2rem);
          line-height: 1;
          font-weight: 900;
          text-align: right;
          padding-top: ${SPACING_TIGHT};
          width: auto;
          height: auto;
          background: transparent;
          border: none;
          border-radius: 0;
          box-shadow: none;
          font-variant-numeric: tabular-nums;
        }

        .process-step-content {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: ${SPACING_TIGHT};
        }

        .process-step-title {
          margin: 0;
          font-size: ${FONT_TITLE3};
          font-weight: 700;
          color: var(--color-text-primary);
        }

        .process-step-description {
          margin: 0;
          padding-left: 0;
          font-size: ${FONT_BODY};
          color: var(--color-text-secondary);
          line-height: 1.65;
          max-width: none;
        }

        .process-connector {
          display: flex;
          align-items: flex-start;
          justify-content: center;
          width: 52px;
          padding-top: ${SPACING_GROUP};
          align-self: flex-start;
        }

        .process-connector .material-icons {
          font-size: 32px;
          color: var(--color-accent);
          opacity: 0.9;
        }

        /* Credibility Section */
        .credibility-section {
          background-color: var(--color-card-background);
        }

        .credibility-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: ${SPACING_GROUP};
        }

        .credibility-item {
          text-align: center;
          padding: ${SPACING_CARD};
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .credibility-icon-container {
          width: 80px;
          height: 80px;
          margin: 0 auto ${SPACING_CARD};
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .credibility-bg-blue {
          background-color: #3B82F6;
        }

        .credibility-bg-purple {
          background-color: #8B5CF6;
        }

        .credibility-bg-amber {
          background-color: #F59E0B;
        }

        .credibility-bg-green {
          background-color: #10B981;
        }

        .credibility-icon {
          font-size: 40px;
          color: #ffffff;
        }

        .credibility-number {
          font-size: 32px;
          font-weight: 700;
          color: #ffffff;
        }

        .credibility-text {
          font-size: ${FONT_HEADLINE};
          font-weight: 600;
          color: var(--color-text-primary);
          margin: 0;
          line-height: 1.4;
          display: flex;
          align-items: center;
        }

        .credibility-link {
          text-decoration: none;
          transition: transform 0.2s ease;
        }

        .credibility-link:hover {
          transform: translateY(-4px);
        }

        /* Testimonials Section */
        .testimonials-container {
          max-width: var(--content-max-width);
          margin: 0 auto;
          padding: 0 var(--content-gutter);
          padding-left: max(var(--content-gutter), env(safe-area-inset-left));
          padding-right: max(var(--content-gutter), env(safe-area-inset-right));
        }

        .testimonials-copy {
          max-width: none;
          margin: 0 0 ${SPACING_GROUP} 0;
        }

        .testimonials-heading {
          text-align: left;
          margin: 0 0 ${SPACING_TIGHT} 0;
        }

        .testimonials-description {
          text-align: left;
          margin: 0;
          max-width: none;
        }

        .testimonials-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          grid-auto-rows: 1fr;
          gap: ${SPACING_BUTTON};
        }

        .testimonial-card {
          grid-column: span 2;
          padding: ${SPACING_BUTTON} ${SPACING_CARD};
          border-radius: ${BORDER_RADIUS};
          background-color: var(--color-surface);
          border: 1px solid var(--color-border);
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: ${SPACING_BUTTON};
          box-sizing: border-box;
        }

        .testimonial-card:nth-child(4) {
          grid-column: 1 / span 2;
        }

        .testimonial-rating {
          display: flex;
          gap: ${SPACING_TIGHT};
        }

        .star-icon {
          font-size: 20px;
          color: #FFB800;
          vertical-align: middle;
        }

        .testimonial-text {
          font-size: ${FONT_BODY};
          color: var(--color-text-primary);
          margin: 0;
          line-height: 1.7;
          font-style: italic;
        }

        .testimonial-text .highlight {
          background-color: #fef08a;
          padding: ${SPACING_MICRO} ${SPACING_TIGHT};
          border-radius: 3px;
          font-style: italic;
        }

        .testimonial-author-section {
          margin-bottom: ${SPACING_BUTTON};
          text-align: left;
        }

        .testimonial-name {
          font-size: ${FONT_SUBHEADLINE};
          color: var(--color-text-primary);
          font-weight: 600;
          margin: 0 0 ${SPACING_TIGHT} 0;
        }

        .testimonial-location {
          font-size: ${FONT_SUBHEADLINE};
          color: var(--color-text-secondary);
          margin: 0;
        }

        /* Contact Section */
        .contact-section {
          background-color: var(--color-card-background);
          scroll-margin-top: 80px;
        }

        .contact-intro {
          font-size: ${FONT_BODY};
          color: var(--color-text-secondary);
          text-align: center;
          margin: 0 auto ${SPACING_GROUP};
          max-width: 800px;
          line-height: 1.7;
        }

        .contact-wrapper {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: ${SPACING_SECTION_LARGE};
          width: 100%;
          max-width: none;
          margin: 0;
          align-items: start;
        }

        .contact-info {
          text-align: left;
        }

        .contact-heading {
          font-size: ${FONT_TITLE};
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0;
          line-height: 1.2;
        }

        .contact-description {
          font-size: ${FONT_BODY};
          color: var(--color-text-secondary);
          margin: 0 0 ${SPACING_CARD_BODY} 0;
          line-height: 1.6;
        }

        .contact-checklist {
          list-style: none;
          padding: 0;
          margin: calc(${SPACING_CARD} - ${SPACING_TIGHT}) 0 0 0;
          text-align: left;
        }

        .contact-checklist li {
          font-size: ${FONT_BODY};
          color: var(--color-text-primary);
          padding: ${SPACING_COMPONENT} 0;
          display: flex;
          align-items: flex-start;
          gap: ${SPACING_BUTTON};
          line-height: 1.5;
        }

        .contact-checklist .check-icon {
          color: var(--color-accent);
          font-size: 20px;
          flex-shrink: 0;
          margin-top: ${SPACING_MICRO};
        }

        .contact-info {
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: ${SPACING_TIGHT};
        }

        .contact-info-title {
          font-size: ${FONT_TITLE3};
          font-weight: 600;
          color: var(--color-text-primary);
          margin: 0 0 ${SPACING_COMPACT} 0;
        }

        .contact-item {
          font-size: ${FONT_BODY};
          color: var(--color-text-primary);
          margin: 0;
        }

        .contact-label {
          font-weight: 600;
          margin-right: ${SPACING_COMPACT};
        }

        .contact-link {
          color: var(--color-accent);
          text-decoration: none;
        }

        .contact-link:hover {
          text-decoration: underline;
        }

        /* 2x2 layout for medium screens */
        @media (max-width: 1100px) {
          .service-area-content {
            align-items: center;
            gap: ${SPACING_BUTTON};
          }

          .service-area-cta-button {
            padding: ${SPACING_BUTTON} ${SPACING_SECTION};
            font-size: 1rem;
          }

          .credibility-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .testimonials-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: ${SPACING_CARD_BODY};
          }

          .hero-section .featured-floors-grid {
            max-width: min(var(--content-max-width), 920px);
          }

          .testimonial-card {
            grid-column: span 1;
          }

          .testimonial-card:nth-child(4) {
            grid-column: span 1;
          }

          .testimonial-card:last-child {
            grid-column: 1 / -1;
          }
        }

        /* Mobile Responsiveness */
        @media (max-width: 768px) {
          .hero-section {
            padding: ${SPACING_PAGE} var(--content-gutter);
            padding-left: max(var(--content-gutter), env(safe-area-inset-left));
            padding-right: max(var(--content-gutter), env(safe-area-inset-right));
          }

          .hero-title {
            font-size: clamp(2rem, 8vw, 2.35rem);
          }

          .hero-subtitle {
            font-size: ${FONT_HEADLINE_MOBILE};
            margin-bottom: ${SPACING_GROUP};
          }

          .service-area-section {
            padding: ${SPACING_PAGE} var(--content-gutter);
            padding-left: max(var(--content-gutter), env(safe-area-inset-left));
            padding-right: max(var(--content-gutter), env(safe-area-inset-right));
          }

          .service-area-content {
            align-items: center;
            gap: ${SPACING_BUTTON};
          }

          .service-area-title {
            gap: 0;
            align-items: center;
            text-align: center;
          }

          .service-area-kicker {
            font-size: 0.82rem;
            letter-spacing: 0.08em;
          }

          .service-area-region {
            font-size: clamp(1.55rem, 8.2vw, 2rem);
          }

          .service-area-cta-button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            max-width: 360px;
            padding: ${SPACING_BUTTON} ${SPACING_CARD_BODY};
            font-size: 1rem;
          }

          .hero-section .featured-floors-grid {
            margin-top: ${SPACING_GROUP};
            gap: ${SPACING_COMPONENT};
            max-width: 680px;
          }

          .hero-section .featured-floor-item {
            aspect-ratio: 4 / 5;
            border-radius: 10px;
          }

          section {
            padding: var(--section-padding-y-mobile) var(--content-gutter);
            padding-left: max(var(--content-gutter), env(safe-area-inset-left));
            padding-right: max(var(--content-gutter), env(safe-area-inset-right));
          }

          .section-heading {
            font-size: ${FONT_TITLE_MOBILE};
          }

          .benefits-grid {
            grid-template-columns: 1fr;
          }

          .benefit-item {
            padding: 0;
          }

          .process-header {
            margin-bottom: 0;
          }

          .process-journey {
            grid-template-columns: 1fr;
            gap: ${SPACING_CARD_BODY};
          }

          .process-connector {
            width: 100%;
            height: 28px;
            justify-content: center;
            padding-top: 0;
          }

          .process-connector .material-icons {
            transform: rotate(90deg);
          }

          .process-step {
            padding: 0;
            gap: ${SPACING_COMPONENT};
          }

          .process-step-number {
            flex-basis: 64px;
            font-size: clamp(2.2rem, 9vw, 3rem);
            padding-top: 3px;
          }

          .process-step-description {
            max-width: none;
          }

          .testimonials-grid {
            grid-template-columns: 1fr;
          }

          .testimonial-card {
            grid-column: span 1;
          }

          .testimonial-card:nth-child(4) {
            grid-column: span 1;
          }

          .testimonial-card:last-child {
            grid-column: span 1;
          }

          .testimonial-card {
            padding: ${SPACING_GROUP} ${SPACING_GROUP};
          }

          .contact-wrapper {
            grid-template-columns: 1fr;
            gap: ${SPACING_GROUP};
          }

          .contact-heading {
            font-size: ${FONT_TITLE_MOBILE};
          }
        }
      `}</style>
    </div>
  );
}
